/**
 * Modern Calls — the redesigned call page, org-gated behind the `modernCalls`
 * power-up. The server decides: when the mapping carries `ModernCall` the page
 * renders on white with the org accent as ink; when it doesn't, every value
 * here falls back to the legacy look so a legacy org renders byte-identically.
 *
 * Mirrors the email side of the design: the org picks the ink, never the
 * paper, and the only filled color on a call is a Mercury response button.
 *
 * Structural types on purpose: both MissionMap and the raw MissionMap_Entity
 * flow through here (Response accepts either), and the helpers only care
 * about the theme keys.
 */

type CallThemeSource = {
  ModernCall?: boolean;
  CompanyColor?: string;
  CallAccentColor?: string;
  CallAccentText?: string;
  CallFontStack?: string;
};

/** The email's default stack — what modern calls render in when the org
 * hasn't picked a typeface. */
export const CALL_FONT_DEFAULT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

type InteractorColors = {
  Color?: string;
  TextColor?: string;
};

/** Near-black used for text on light fills; matches the studio foreground. */
const INK = "#16181d";

/** The legacy full-bleed ground (company color, navy fallback). */
const LEGACY_GROUND = "rgb(2, 28, 51)";

export type CallTheme = {
  modern: boolean;
  /** Page ground: white when modern, company color/navy when legacy. */
  ground: string;
  /** `theme-color` meta so the browser chrome follows the page. */
  themeColor: string;
  /** Raw org accent — the masthead rule. */
  accent: string;
  /** Accent corrected for legibility as text on white — the masthead name. */
  accentText: string;
  /** The org's typeface (email-safe stack); only consumed by modern calls. */
  fontStack: string;
};

export function callTheme(mission?: CallThemeSource): CallTheme {
  const legacyGround = mission?.CompanyColor
    ? mission.CompanyColor
    : LEGACY_GROUND;

  if (mission?.ModernCall !== true) {
    return {
      modern: false,
      ground: legacyGround,
      themeColor: legacyGround,
      accent: legacyGround,
      accentText: legacyGround,
      fontStack: CALL_FONT_DEFAULT,
    };
  }

  const accent = mission.CallAccentColor ?? "#0d1b2f";

  return {
    modern: true,
    ground: "#ffffff",
    themeColor: "#ffffff",
    accent,
    accentText: mission.CallAccentText ?? accent,
    fontStack: mission.CallFontStack || CALL_FONT_DEFAULT,
  };
}

/**
 * Label color for a response button (text and icon alike — the icon inherits
 * currentColor). The fill is Mercury's — each interactor carries its own
 * Color and this never changes it. Labels are WHITE by design decision; the
 * single exception is a genuinely light fill, where white would be invisible,
 * which falls back to ink.
 *
 * The server derives TextColor on v3 mission payloads; the v2 assignment path
 * drops it in a struct conversion, so this recomputes locally when absent.
 */
export function interactorLabelColor(
  mission: CallThemeSource | undefined,
  interactor: InteractorColors,
): string {
  if (mission?.ModernCall !== true) return "white";
  if (interactor.TextColor) return interactor.TextColor;

  return labelOnFill(interactor.Color);
}

/** White unless the fill is light enough to swallow it (BT.601, threshold
 * 160 — the same heuristic the backend and the footer palette use). */
export function labelOnFill(fill?: string): string {
  const rgb = parseColor(fill);
  if (!rgb) return "white";

  const [r, g, b] = rgb;
  const brightness = 0.299 * r + 0.587 * g + 0.114 * b;

  return brightness > 160 ? INK : "white";
}

/**
 * Text color for the "answered" state, where the chosen interactor shows as
 * colored text on a white fill. Legacy keeps the raw interactor color (the
 * historical look); modern darkens it until it reads (WCAG AA).
 */
export function interactorTextOnWhite(
  mission: CallThemeSource | undefined,
  interactor: InteractorColors,
): string | undefined {
  if (mission?.ModernCall !== true) return interactor.Color;

  return readableOnWhite(interactor.Color);
}

/**
 * Darkens a color until it clears WCAG AA (4.5:1) as text on white — for the
 * modern "answered" button state, where the choice shows as colored text on a
 * white fill. Mirrors the server-side darkenForTextOnWhite step-for-step.
 */
export function readableOnWhite(color?: string): string {
  const rgb = parseColor(color);
  if (!rgb) return INK;

  let [r, g, b] = rgb;
  for (let i = 0; i < 40; i++) {
    if (contrastRatio(1.0, relativeLuminance([r, g, b])) >= 4.5) break;
    r *= 0.92;
    g *= 0.92;
    b *= 0.92;
  }

  const hex = (v: number) =>
    Math.round(v).toString(16).padStart(2, "0");

  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

function parseColor(color?: string): [number, number, number] | undefined {
  if (!color) return undefined;

  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
  if (hex) {
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex;
    return [
      parseInt(full.slice(0, 2), 16),
      parseInt(full.slice(2, 4), 16),
      parseInt(full.slice(4, 6), 16),
    ];
  }

  const rgb = color
    .trim()
    .match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  }

  return undefined;
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastRatio(l1: number, l2: number): number {
  const hi = Math.max(l1, l2);
  const lo = Math.min(l1, l2);

  return (hi + 0.05) / (lo + 0.05);
}
